type InspectionSummaryItem = {
  id?: number | null;
  inspectionType?: string | null;
  isDone?: boolean | null;
  subInspectionType?: string | null;
  createdAt?: string | null;
};

export type InspectionProgressResult = {
  progressLabel: string;
  progressColor: string;
} | null;

const SUB_STAGE_FALLBACK_LABELS = ["초품", "중품", "종품"];

const SUB_STAGE_LABELS: Record<string, string> = {
  FIRST: "초품",
  MIDDLE: "중품",
  LAST: "종품",
};

const SUB_STAGE_ORDER: Record<string, number> = {
  FIRST: 0,
  MIDDLE: 1,
  LAST: 2,
};

const PROGRESS_STAGE_COLOR_MAP = {
  "초품 진행": "orange",
  "중품 진행": "yellow",
  "종품 진행": "grape",
  "검사 완료": "green",
} as const;

export const getInspectionProgress = (
  inspections: InspectionSummaryItem[] | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string
): InspectionProgressResult => {
  if (!inspections?.length) {
    return null;
  }

  const sortedInspections = [...inspections].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

    if (aTime !== bTime) {
      return aTime - bTime;
    }

    return (a.id ?? 0) - (b.id ?? 0);
  });

  const getSubInspectionStageIndex = (inspection: InspectionSummaryItem, fallbackIndex: number) => {
    if (inspection.subInspectionType && inspection.subInspectionType in SUB_STAGE_ORDER) {
      return SUB_STAGE_ORDER[inspection.subInspectionType];
    }

    return Math.min(fallbackIndex, SUB_STAGE_FALLBACK_LABELS.length - 1);
  };

  const getStageLabel = (inspection: InspectionSummaryItem | undefined, fallbackIndex: number) => {
    return (
      (inspection?.subInspectionType && SUB_STAGE_LABELS[inspection.subInspectionType]) ||
      SUB_STAGE_FALLBACK_LABELS[fallbackIndex] ||
      t("inspectionType.SUB_IN_PROCESS")
    );
  };

  const latestMainInspectionIndex = [...sortedInspections]
    .reverse()
    .findIndex((inspection) => inspection.inspectionType === "IN_PROGRESS");
  const subInspections = sortedInspections.filter(
    (inspection) => inspection.inspectionType === "SUB_IN_PROCESS"
  );

  if (latestMainInspectionIndex < 0) {
    if (!subInspections.length) {
      return null;
    }

    const incompleteSubInspections = subInspections.filter((inspection) => !inspection.isDone);
    const fallbackStage = incompleteSubInspections.sort((a, b) => {
      const aIndex = getSubInspectionStageIndex(a, subInspections.indexOf(a));
      const bIndex = getSubInspectionStageIndex(b, subInspections.indexOf(b));
      return aIndex - bIndex;
    })[0];
    const fallbackStageIndex = fallbackStage
      ? getSubInspectionStageIndex(fallbackStage, subInspections.indexOf(fallbackStage))
      : Math.min(subInspections.length, SUB_STAGE_FALLBACK_LABELS.length - 1);
    const fallbackStageLabel = getStageLabel(fallbackStage, fallbackStageIndex);

    return subInspections.every((inspection) => inspection.isDone)
      ? {
          progressLabel: "검사 완료",
          progressColor: PROGRESS_STAGE_COLOR_MAP["검사 완료"],
        }
      : {
          progressLabel: `${fallbackStageLabel} 진행`,
          progressColor:
            PROGRESS_STAGE_COLOR_MAP[
              `${fallbackStageLabel} 진행` as keyof typeof PROGRESS_STAGE_COLOR_MAP
            ] ?? "blue",
        };
  }

  const lastMainInspectionPosition = sortedInspections.length - 1 - latestMainInspectionIndex;
  const lastMainInspection = sortedInspections[lastMainInspectionPosition];
  let lastSubInspections = sortedInspections
    .slice(lastMainInspectionPosition + 1)
    .filter((inspection) => inspection.inspectionType === "SUB_IN_PROCESS")
    .slice(0, 3);

  if (!lastSubInspections.length && subInspections.length) {
    lastSubInspections = subInspections.slice(-3);
  }

  const incompleteLastSubInspections = lastSubInspections.filter(
    (inspection) => !inspection.isDone
  );
  const isLastInspectionComplete =
    !!lastMainInspection?.isDone &&
    lastSubInspections.length === 3 &&
    lastSubInspections.every((inspection) => inspection.isDone);

  if (isLastInspectionComplete) {
    return {
      progressLabel: "검사 완료",
      progressColor: PROGRESS_STAGE_COLOR_MAP["검사 완료"],
    };
  }

  const activeStage = incompleteLastSubInspections.sort((a, b) => {
    const aIndex = getSubInspectionStageIndex(a, lastSubInspections.indexOf(a));
    const bIndex = getSubInspectionStageIndex(b, lastSubInspections.indexOf(b));
    return aIndex - bIndex;
  })[0];
  const activeStageIndex = activeStage
    ? getSubInspectionStageIndex(activeStage, lastSubInspections.indexOf(activeStage))
    : Math.min(lastSubInspections.length, SUB_STAGE_FALLBACK_LABELS.length - 1);
  const stageLabel = getStageLabel(activeStage, activeStageIndex);

  const progressLabel = `${stageLabel} 진행`;

  return {
    progressLabel,
    progressColor:
      PROGRESS_STAGE_COLOR_MAP[progressLabel as keyof typeof PROGRESS_STAGE_COLOR_MAP] ?? "blue",
  };
};

export type { InspectionSummaryItem };
